import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NotifierService } from 'angular-notifier';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AnyIntegration, IIntegrationBostonPizzaEventsAPI } from '../../../../interfaces/restaurant';

type EditIntegrationBpEventsApiForm = {
  proxy: FormControl<boolean>;
  proxyUnifiedMode: FormControl<boolean>;
  chapterId: FormControl<string>;
  chapterLocation: FormControl<string>;
  chapterTitle: FormControl<string>;
  slug: FormControl<string>;
};

@UntilDestroy()
@Component({
  selector: 'app-edit-integration-bp-events-api',
  templateUrl: './edit-integration-bp-events-api.component.html',
  styleUrls: ['./edit-integration-bp-events-api.component.scss']
})
export class EditIntegrationBpEventsApiComponent implements OnInit {

  @Input() set data(value: AnyIntegration) {

    if (value && value.provider === 'bostonPizzaEventsAPI') {
      const bpEventsApiValue = value as IIntegrationBostonPizzaEventsAPI;

      this.editIntegrationForm.get('proxy')?.setValue(bpEventsApiValue.proxy ?? false, { emitEvent: false });
      const proxyUnifiedModeControl = this.editIntegrationForm.get('proxyUnifiedMode');

      if (bpEventsApiValue.proxy) {
        proxyUnifiedModeControl?.enable({ emitEvent: false });
        proxyUnifiedModeControl?.setValue(bpEventsApiValue.proxyUnifiedMode ?? false, { emitEvent: false });
      } else {
        proxyUnifiedModeControl?.disable({ emitEvent: false });
        proxyUnifiedModeControl?.setValue(false, { emitEvent: false });
      }

      this.editIntegrationForm.patchValue(bpEventsApiValue.credentials);
    }
  }

  public editIntegrationForm: FormGroup<EditIntegrationBpEventsApiForm> = new FormGroup({
      proxy: new FormControl<boolean>(false, { nonNullable: true, validators: [Validators.required] }),
      proxyUnifiedMode: new FormControl(
        {value: false, disabled: true},
        { nonNullable: true, validators: [Validators.required] }
      ),
      chapterId: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(255)] }),
      chapterLocation: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(255)] }),
      chapterTitle: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(255)] }),
      slug: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(255)] })
    }
  );

  @Output() edited = new EventEmitter<Partial<AnyIntegration>>();
  @Output() remove = new EventEmitter<void>();

  constructor(
    public dialogRef: MatDialogRef<any>,
    public notifier: NotifierService,
  ) {}

  ngOnInit(): void {
    this.addListeners();
  }

  private addListeners(): void {
    this.editIntegrationForm.get('proxy')?.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe((proxyValue: boolean) => {
      const proxyUnifiedModeControl = this.editIntegrationForm.get('proxyUnifiedMode');

      if (!proxyValue) {
        proxyUnifiedModeControl?.disable({emitEvent: false});
        proxyUnifiedModeControl?.setValue(false, { emitEvent: false });
      } else {
        proxyUnifiedModeControl?.enable({emitEvent: false});
      }
    });
  }

  public edit(): void {
    const formValue = this.editIntegrationForm.getRawValue();

    this.edited.emit({
      proxy: formValue.proxy,
      proxyUnifiedMode: formValue.proxyUnifiedMode,
      credentials: {
        chapterId: formValue.chapterId,
        chapterLocation: formValue.chapterLocation,
        chapterTitle: formValue.chapterTitle,
        slug: formValue.slug,
      }
    });
  }

  public removeIntegration(): void {
    this.remove.emit();
  }
}
